import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import { toast } from "sonner";
import { Plus, Check, Trash2 } from "lucide-react";

const BUSINESSES = ["PYN CAPITAL", "DOA / PRO PARTNER", "BIOVAULT", "PERSONAL"];

const heat = (days) => (days >= 7 ? "red" : days >= 3 ? "amber" : "green");
const HEAT_TEXT = { green: "text-[#22c55e]", amber: "text-[#F5A623]", red: "text-[#ef4444]" };
const HEAT_BORDER = { green: "border-l-[#22c55e]/70", amber: "border-l-[#F5A623]/70", red: "border-l-[#ef4444]/70" };

const NewForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ person: "", company: "", business: "PYN CAPITAL", waiting_for: "", follow_up_date: "", days_waiting: 0 });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.person || !f.waiting_for) return;
    await api.createWaitingV2({ ...f, days_waiting: parseInt(f.days_waiting || 0, 10) });
    toast("Added");
    setF({ ...f, person: "", company: "", waiting_for: "", follow_up_date: "", days_waiting: 0 });
    setOpen(false);
    onCreated();
  };
  return open ? (
    <form onSubmit={submit} className="op-panel p-6 space-y-4 mb-8" data-testid="waiting-new-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input placeholder="Person" value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })} className="op-input" data-testid="waiting-person" />
        <input placeholder="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="op-input" />
        <select value={f.business} onChange={(e) => setF({ ...f, business: e.target.value })} className="op-input op-mono text-xs">
          {BUSINESSES.map((b) => <option key={b}>{b}</option>)}
        </select>
        <input placeholder="What you're waiting for" value={f.waiting_for} onChange={(e) => setF({ ...f, waiting_for: e.target.value })} className="op-input md:col-span-2" data-testid="waiting-what" />
        <input type="date" value={f.follow_up_date} onChange={(e) => setF({ ...f, follow_up_date: e.target.value })} className="op-input op-mono" />
        <input type="number" placeholder="Days" value={f.days_waiting} onChange={(e) => setF({ ...f, days_waiting: e.target.value })} className="op-input op-mono" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="op-btn">CANCEL</button>
        <button type="submit" className="op-btn op-btn--primary" data-testid="waiting-new-submit">SAVE</button>
      </div>
    </form>
  ) : (
    <button onClick={() => setOpen(true)} data-testid="waiting-new" className="op-btn inline-flex items-center gap-2 mb-8">
      <Plus size={12} /> NEW PERSON / PROMISE
    </button>
  );
};

const WaitingOnPage = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.waitingV2().then(setRows);
  useEffect(() => { load(); }, []);

  const resolve = async (r) => {
    await api.patchWaitingV2(r.id, { status: r.status === "RESOLVED" ? "OPEN" : "RESOLVED" });
    load();
  };
  const del = async (r) => {
    await api.deleteWaitingV2(r.id);
    load();
  };

  const grouped = useMemo(() => {
    const map = new Map();
    rows
      .filter((r) => r.status !== "RESOLVED")
      .forEach((r) => {
        if (!map.has(r.person)) map.set(r.person, []);
        map.get(r.person).push(r);
      });
    // Sort by max days waiting per person, desc
    return [...map.entries()].sort((a, b) => {
      const ma = Math.max(...a[1].map((x) => x.days_waiting || 0));
      const mb = Math.max(...b[1].map((x) => x.days_waiting || 0));
      return mb - ma;
    });
  }, [rows]);

  const resolved = rows.filter((r) => r.status === "RESOLVED");

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1600px] mx-auto space-y-12" data-testid="waiting-page">
      <header>
        <div className="op-microcopy text-[#00E5FF] mb-4">DEPENDENCIES · PEOPLE</div>
        <h1 className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3">Waiting on.</h1>
        <p className="text-[#A0A5B5] text-lg max-w-2xl">
          John. Ty. Dr. Wachtel. Nothing slips.
        </p>
        <div className="op-hairline mt-8" />
      </header>

      <NewForm onCreated={load} />

      {grouped.length === 0 && (
        <div className="op-panel p-8 text-center text-[#5E657D] text-sm" data-testid="waiting-empty">
          No open dependencies. Nice.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="waiting-grid">
        {grouped.map(([person, items]) => {
          const maxDays = Math.max(...items.map((i) => i.days_waiting || 0));
          const h = heat(maxDays);
          return (
            <div key={person} className={`op-panel p-6 border-l-4 ${HEAT_BORDER[h]}`} data-testid={`waiting-person-${person}`}>
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="op-tag mb-1">{items[0].company || items[0].business}</div>
                  <h3 className="text-white text-2xl font-light tracking-tight">{person}</h3>
                </div>
                <div className={`op-mono text-sm ${HEAT_TEXT[h]}`}>{maxDays}d</div>
              </div>
              <ul className="space-y-3">
                {items.map((r) => (
                  <li key={r.id} className="flex items-start gap-3 group">
                    <button
                      onClick={() => resolve(r)}
                      className="op-check mt-0.5"
                      data-checked={r.status === "RESOLVED"}
                      data-testid={`waiting-resolve-${r.id}`}
                      aria-label="Resolve"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm">{r.waiting_for}</div>
                      {r.notes && <div className="text-[#5E657D] text-xs mt-0.5">{r.notes}</div>}
                      <div className="op-tag mt-1">
                        {r.business}
                        {r.follow_up_date && ` · FOLLOW-UP ${r.follow_up_date}`}
                        {` · ${r.days_waiting}d`}
                      </div>
                    </div>
                    <button
                      onClick={() => del(r)}
                      className="text-[#5E657D] hover:text-[#ef4444] opacity-0 group-hover:opacity-100 transition"
                      data-testid={`waiting-del-${r.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {resolved.length > 0 && (
        <section data-testid="waiting-resolved">
          <SectionHeader code="R" label="RESOLVED" caption="Closed dependencies — reopen from here if needed." />
          <div className="op-panel divide-y divide-white/[0.04]">
            {resolved.map((r) => (
              <div key={r.id} className="px-6 py-3 flex items-center gap-3 opacity-60">
                <button onClick={() => resolve(r)} className="op-check" data-checked />
                <span className="text-white text-sm flex-1 line-through">{r.person} — {r.waiting_for}</span>
                <button onClick={() => del(r)} className="text-[#5E657D] hover:text-[#ef4444]">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default WaitingOnPage;
